import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { type PropsWithChildren, useRef, useState } from "react";
import { useSnapshot } from "valtio";

import { toast } from "sonner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding } from "@fortawesome/free-regular-svg-icons";

import {
  faAt,
  faCircleInfo,
  faGlobe,
  faPalette,
  faPen,
  faPerson,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { Organization, ProfileContactDetails } from "../../../../../client";
import { salesContactState } from "../../../../../state/salesContact";
import { SalesContactDrawerHeader } from "../../../../profile/SalesContactDrawerHeader";
import { LeadRowCard } from "../card/LeadRowCard";
import { ImageUpload } from "../../../../../types";
import { ImageCropperDialog } from "../../../../../components/ImageCropperDialog";
import { updateOrganizationLogo } from "../../../../../requests";
import { organizationInfoState } from "../../../../../state/organizationInfo";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  Input,
  isEmailOrEmpty,
  isPhoneNumberOrEmpty,
  isString,
} from "../../../../../components";
import { isURL } from "validator";
import { useParams } from "react-router";
import { useGetOrganizationInfo } from "../../../../../services/organtization/get-organization-info";
import { OrganizationInfoDrawerHeader } from "./OrganizationInfoDrawerHeader";
import { getImageUrl } from "../../../../../utils/image";
import frogLogo from "../../../../../assets/frog_logo.png";
import defaultMainLogo from "../../../../../assets/default-main-logo.png";
import defaultIconLogo from "../../../../../assets/default-icon-logo.png";
import { useUpdateOrganization } from "../../../../../services/organtization/update-organization";
import { cn } from "../../../../../components/utils/cn";
import { contactInfoSalesContactState } from "../../../../../state/contactInfoSalesContact";

interface Props extends PropsWithChildren {}

export const ContactDetailsDrawer = ({ children }: Props) => {
  const toastId = useRef<string | number>();

  const { organizationId } = useParams();

  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [salesContact, setSalesContact] = useState<string>("");
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string>("");

  const { showContactInfoSalesContact } = useSnapshot(
    contactInfoSalesContactState,
  );

  const { data: organizationInfo, refetch: refetchOrgInfo } =
    useGetOrganizationInfo(organizationId ?? "");

  const updateOrgProfile = useUpdateOrganization(
    {
      onSuccess: () => {
        refetchOrgInfo(); // Trigger refetch her
        toast.success("Profile status updated successfully");
      },
    },
    organizationId ?? "",
  );

  if (!organizationId) {
    toast.error("Missing required information");
    return null;
  }

  const handleOrganizationInfoUpdate = (
    updateOrganizationInfo: Organization,
  ) => {
    updateOrgProfile.mutate({
      path: { organizationID: organizationId },
      body: updateOrganizationInfo,
    });
  };

  const handleClose = () => {
    contactInfoSalesContactState.showContactInfoSalesContact = false;
  };

  return (
    <Drawer
      direction="right"
      open={!!showContactInfoSalesContact}
      onClose={handleClose}
      shouldScaleBackground={false}
    >
      {children}

      <DrawerContent
        className="min-w-[50%] max-w-[50%] flex flex-col pb-8 overflow-y-scroll"
        direction="right"
        data-vaul-no-drag
      >
        <VisuallyHidden.Root>
          <DrawerTitle>Edit Contact Detials</DrawerTitle>
          <DrawerDescription>
            Edit contact details to fit your brand needs.
          </DrawerDescription>
        </VisuallyHidden.Root>

        <OrganizationInfoDrawerHeader
          handleCreateClick={() => {}}
          title="Edit sales contact details"
          handleClose={handleClose}
          emailIsValid={true}
        />
        {organizationInfo && (
          <div className="flex flex-col justify-center mt-4 px-8 pb-8">
            <div className="flex flex-col gap-8 mt-8">
              <div>
                <h6 className="text-sm text-gray-6">Sales contact name</h6>
                <Input
                  className=""
                  placeholder="Your name"
                  leftIcon={faPerson}
                  value={(salesContact || organizationInfo.salesContact) ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSalesContact(e.target.value);
                  }}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    handleOrganizationInfoUpdate({
                      ...organizationInfo,
                      salesContact: inputValue,
                    });
                  }}
                  validate={isString}
                />
              </div>

              <div>
                <h6 className="text-sm text-gray-6">Email</h6>
                <Input
                  className=""
                  placeholder="Email"
                  leftIcon={faGlobe}
                  value={(email || organizationInfo?.email) ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEmail(e.target.value);
                  }}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    handleOrganizationInfoUpdate({
                      ...organizationInfo,
                      email: inputValue,
                    });
                  }}
                  validate={isEmailOrEmpty}
                />
              </div>

              <div>
                <h6 className="text-sm text-gray-6">
                  Sales contact phone number
                </h6>
                <Input
                  className=""
                  placeholder="Phone"
                  leftIcon={faPerson}
                  value={(phoneNumber || organizationInfo.phoneNumber) ?? ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPhoneNumber(e.target.value);
                  }}
                  onBlur={(
                    event: React.FocusEvent<
                      HTMLInputElement | HTMLTextAreaElement
                    >,
                  ) => {
                    const inputValue = event.target.value;
                    handleOrganizationInfoUpdate({
                      ...organizationInfo,
                      phoneNumber: inputValue,
                    });
                  }}
                  validate={isPhoneNumberOrEmpty}
                />
              </div>
            </div>
            <input
              className="hidden"
              type="file"
              id={"logo-uploader"}
              onChange={(event) => {
                if (event.target.files === null) return;

                const file = event.target.files[0];
                const reader = new FileReader();

                reader.onload = (e: ProgressEvent<FileReader>) => {
                  const formatInfo = (e.target?.result as string).split(",")[0];
                  const base64String = (e.target?.result as string).split(
                    ",",
                  )[1];
                  const format = formatInfo.split(";")[0].split(":")[1];

                  const imageUpload: ImageUpload = {
                    fileType: formatInfo,
                    base64File: base64String,
                  };

                  setImageToCrop(imageUpload.base64File);
                  setShowCropper(true);
                };

                reader.readAsDataURL(file);
              }}
            ></input>
            {showCropper && (
              <ImageCropperDialog
                open={showCropper}
                onClose={() => setShowCropper(false)}
                image={imageToCrop}
                onCrop={(croppedImage: string) => {
                  const imageUpload: ImageUpload = {
                    fileType: "image/jpeg",
                    base64File: croppedImage.split(",")[1],
                  };

                  updateOrganizationLogo(
                    organizationId,
                    imageUpload,
                    false,
                  ).then((newOrganizationLogoId: string) => {
                    refetchOrgInfo();
                  });
                }}
                aspectRatio={1}
              />
            )}
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
};
